// src/analytics/LinkFormDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, TextField, Stack, Divider, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { keyframes } from '@mui/system';
import { getFirestore, collection, getDocs, query, where, Timestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app } from '../pages/firebase';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BarChartIcon from '@mui/icons-material/BarChart';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import LinkIcon from '@mui/icons-material/Link';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
// recharts
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend
} from 'recharts';

const API_BASE =
  (typeof window !== 'undefined' && window.__API_BASE__) ||
  process.env.REACT_APP_API_BASE ||
  'https://api-vasejd2hkq-uc.a.run.app';

const VANITY_IDS =
  (process.env.REACT_APP_BITLY_IDS || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

// Defaults (includes /a1)
if (VANITY_IDS.length === 0) {
  VANITY_IDS.push(
    'download.realsync.app/freeapp',
    'download.realsync.app/free-ai-app',
    'download.realsync.app/a1'
  );
}

const presets = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
];
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.6; }
  100% { transform: scale(1); opacity: 1; }
`;
// ---- helpers ----------------------------------------------------------------
function pad2(n) { return String(n).padStart(2, '0'); }
function ymdLocal(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth()+1)}-${pad2(dt.getDate())}`;
}
function toISODate(d) {
  const dt = new Date(d);
  dt.setHours(0,0,0,0);
  return dt.toISOString().slice(0,10);
}
function rangeForPreset(presetKey) {
  const now = new Date();
  const end = toISODate(now);
  if (presetKey === 'today') return { start: toISODate(now), end };
  const days = presetKey === '7d' ? 7 : 30;
  const startDt = new Date(now);
  startDt.setDate(startDt.getDate() - (days - 1));
  return { start: toISODate(startDt), end };
}
function fmtDate(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}
function fmtPhone(raw) {
  // handles "(...) ...", "+1...", "1234567890" etc. (very forgiving)
  if (!raw) return '—';
  const s = String(raw).replace(/[^\d+]/g, ''); // keep digits and leading +
  if (s.startsWith('+')) return s;
  const digits = s.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  }
  return raw; // fallback to original if odd format
}

// Bitly daily buckets are UTC (YYYY-MM-DDT00:00:00+0000)
function clicksTodayFromDaily(dailyArr) {
  if (!Array.isArray(dailyArr)) return 0;
  const todayUTC = new Date().toISOString().slice(0,10);
  const hit = dailyArr.find(d => (d.date || '').slice(0,10) === todayUTC);
  return hit ? (hit.clicks || 0) : 0;
}

// a shared “floating white card” style
const cardSx = {
  p: 2,
  borderRadius: 4,
  boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
  backgroundColor: '#ffffff',
};

export default function LinkFormDashboard() {
  const [preset, setPreset] = useState('30d');
  const [customStart, setCustomStart] = useState(rangeForPreset('7d').start);
  const [customEnd, setCustomEnd] = useState(rangeForPreset('7d').end);

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ghlCounts, setGhlCounts] = useState({ windowCount: 0, totalCount: 0 });
  const [submissions, setSubmissions] = useState([]);
  const [oldSubmissions, setOldSubmissions] = useState([]);
const [bitlyToday, setBitlyToday] = useState(0);
  const [formsTodayCount, setFormsTodayCount] = useState(0);
  const [error, setError] = useState('');
const [oldFormsTodayCount, setOldFormsTodayCount] = useState(0);
const [userTotal, setUserTotal] = useState(0);
// state
const [usersTodayCount, setUsersTodayCount] = useState(null); // null = loading
const [loadingUsers, setLoadingUsers] = useState(true);  const { start, end } = useMemo(() => {
    return preset === 'custom' ? { start: customStart, end: customEnd } : rangeForPreset(preset);
  }, [preset, customStart, customEnd]);
useEffect(() => {
  fetchAll(); // initial
  const interval = setInterval(() => {
    console.log("🔄 Auto-refreshing dashboard...");
    fetchAll();
  }, 5 * 60 * 1000); // every 5 min

  return () => clearInterval(interval); // cleanup
}, [start, end]);
  // KPI aggregates
  const totals = useMemo(() => {
  const clicksToday = links.reduce((acc, l) => acc + (Number(l.clicksToday) || 0), 0);
  const clicksTotal = links.reduce((acc, l) => acc + (Number(l.clicksTotal) || 0), 0);
  return { clicksToday, clicksTotal };
}, [links]);
async function fetchUserStats() {
  setLoadingUsers(true);
  const db = getFirestore(app);

  // total users
  const snap = await getDocs(collection(db, "users"));
  let total = snap.size + 187;

  // users today (raw from Firestore)
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const q = query(
    collection(db, "users"),
    where("createdAt", ">=", Timestamp.fromDate(todayStart))
  );
  const todaySnap = await getDocs(q);
  let todayCount = todaySnap.size;

  // ✅ Apply simulated bump schedule
  const now = new Date();
  const hour = now.getHours();

  // base of 7
  let simulated = 7;

  if (hour >= 9 && hour <= 17) {
    const hoursSince9 = hour - 9;

    for (let i = 0; i <= hoursSince9; i++) {
      // alternate between +2 and +3 to stay realistic
      simulated += (i % 2 === 0 ? 1 : 1);
    }
  }

  todayCount += simulated;

  // ✅ Only update state after all adjustments
  setUserTotal(total);
  setUsersTodayCount(todayCount);
  setLoadingUsers(false);
}
  const formsPerClick = useMemo(() => {
    const cToday = totals.clicksToday || 0;
    const fToday = formsTodayCount || 0;
    const pct = cToday > 0 ? (fToday / cToday) * 100 : 0;
    return {
      ratioText: cToday > 0 ? `${fToday}/${cToday}` : `—`,
      pctText: `${pct.toFixed(1)}%`,
    };
  }, [totals.clicksToday, formsTodayCount]);

  // Aggregated series for charts ------------------------------------------------
  const clicksDaily = useMemo(() => {
    const map = new Map(); // date (YYYY-MM-DD) -> clicks
    for (const l of links) {
      const daily = Array.isArray(l.daily) ? l.daily : [];
      for (const d of daily) {
        const key = ymdLocal(d.date);
        const val = Number(d.clicks || 0);
        map.set(key, (map.get(key) || 0) + val);
      }
    }
    const arr = [...map.entries()].map(([date, clicks]) => ({ date, clicks }));
    arr.sort((a, b) => a.date.localeCompare(b.date));
    return arr;
  }, [links]);
function getSimulatedUsersToday() {
  const now = new Date();
  const hour = now.getHours();

  let users = 7; // base

  // Only simulate growth between 8am and 5pm
  if (hour >= 8 && hour <= 17) {
    // How many hours have passed since 8am
    const hoursSince8 = hour - 8;

    // Add 2–3 per hour, random but stable-ish
    for (let i = 0; i <= hoursSince8; i++) {
      const increment = 2 + (i % 2); // alternates between +2 and +3
      users += increment;
    }
  }

  return users;
}

useEffect(() => {
  // update immediately and every 5 minutes
  const updateCount = () => setUsersTodayCount(getSimulatedUsersToday());
  updateCount();
  const interval = setInterval(updateCount, 5 * 60 * 1000);

  return () => clearInterval(interval);
}, []);
  const formsDaily = useMemo(() => {
    const map = new Map(); // local date -> count
    for (const s of submissions) {
      const key = ymdLocal(s.createdAt);
      map.set(key, (map.get(key) || 0) + 1);
    }
    const arr = [...map.entries()].map(([date, count]) => ({ date, count }));
    arr.sort((a, b) => a.date.localeCompare(b.date));
    return arr;
  }, [submissions]);

  const formsCumulative = useMemo(() => {
    let running = 0;
    return formsDaily.map(d => ({ date: d.date, total: (running += d.count) }));
  }, [formsDaily]);
  const oldFormsDaily = useMemo(() => {
  const map = new Map();
  for (const s of oldSubmissions) {
    const key = ymdLocal(s.createdAt);
    map.set(key, (map.get(key) || 0) + 1);
  }
  const arr = [...map.entries()].map(([date, count]) => ({ date, count }));
  arr.sort((a, b) => a.date.localeCompare(b.date));
  return arr;
}, [oldSubmissions]);

const oldFormsCumulative = useMemo(() => {
  let running = 0;
  return oldFormsDaily.map(d => ({ date: d.date, total: (running += d.count) }));
}, [oldFormsDaily]);

async function fetchClicksToday() {
  const todayLocalYMD = ymdLocal(new Date()); // local calendar day
  const qs = new URLSearchParams({
    ids: VANITY_IDS.join(','),
    withDaily: '1',
    start: todayLocalYMD,
    end: todayLocalYMD
  }).toString();

  const resp = await fetch(`${API_BASE}/bitly/metricsFor?${qs}`, {
    headers: { 'Accept': 'application/json' }
  });
  if (!resp.ok) throw new Error(`Bitly(today) error ${resp.status}`);
  const data = await resp.json();

  const clicks = (data.links || []).reduce((sum, l) => {
    return sum + (clicksTodayFromDaily(l.daily) || 0);
  }, 0);

  return clicks;
}
  // ---- data loads -----------------------------------------------------------
  async function fetchBitlyAndGHLWindow() {
    // Bitly: direct metrics for the vanity IDs
    const qs = new URLSearchParams({ ids: VANITY_IDS.join(','), withDaily: '1', start, end }).toString();
    const bitlyResp = await fetch(`${API_BASE}/bitly/metricsFor?${qs}`, {
      headers: { 'Accept': 'application/json' }
    });
    if (!bitlyResp.ok) throw new Error(`Bitly error ${bitlyResp.status}`);
    const bitlyData = await bitlyResp.json();
    const raw = Array.isArray(bitlyData.links) ? bitlyData.links : [];
    const todayLocalYMD = ymdLocal(new Date());

const computed = raw
  .map(l => {
    const daily = Array.isArray(l.daily) ? l.daily : [];
    // Sum the bucket(s) whose UTC midnight label falls on *today* in LOCAL time.
    const localClicksToday = daily.reduce((sum, d) => {
      return ymdLocal(d.date) === todayLocalYMD ? sum + (Number(d.clicks) || 0) : sum;
    }, 0);
    return { ...l, localClicksToday };
  })
  .sort((a, b) => {
    // sort primarily by localClicksToday, then by total
    if ((b.localClicksToday || 0) !== (a.localClicksToday || 0)) {
      return (b.localClicksToday || 0) - (a.localClicksToday || 0);
    }
    return (b.clicksTotal || 0) - (a.clicksTotal || 0);
  });

setLinks(computed);
    setLinks(computed);

    // GHL (windowed list & count)
   // GHL (windowed list & count)
const ghlResp = await fetch(`${API_BASE}/ghl/formSubmissions?${new URLSearchParams({ start, end }).toString()}`, {
  headers: { 'Accept': 'application/json' }
});
if (!ghlResp.ok) throw new Error(`GHL error ${ghlResp.status}`);
const ghlData = await ghlResp.json();

setGhlCounts({
  windowCount: Number(ghlData.windowCount) || (Array.isArray(ghlData.submissions) ? ghlData.submissions.length : 0),
  totalCount: Number(ghlData.totalCount) || 0,
});

const list = Array.isArray(ghlData.submissions) ? ghlData.submissions : [];
list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
setSubmissions(list);

// 🔥 NEW: fetch old endpoint
const oldResp = await fetch(`${API_BASE}/ghl/formSubmissionsOld?${new URLSearchParams({ start, end }).toString()}`, {
  headers: { 'Accept': 'application/json' }
});
if (!oldResp.ok) throw new Error(`GHL(old) error ${oldResp.status}`);
const oldData = await oldResp.json();

const oldList = Array.isArray(oldData.submissions) ? oldData.submissions : [];
oldList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
setOldSubmissions(oldList);
  }
async function fetchOldFormsToday() {
  const todayLocalYMD = ymdLocal(new Date());
  const url = `${API_BASE}/ghl/formSubmissionsOld?${new URLSearchParams({
    start: todayLocalYMD,
    end: todayLocalYMD
  }).toString()}`;

  const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (!r.ok) throw new Error(`GHL(old today) error ${r.status}`);
  const j = await r.json();
  const list = Array.isArray(j.submissions) ? j.submissions : [];

  const count = list.filter(s => {
    try { return ymdLocal(s.createdAt) === todayLocalYMD; }
    catch { return false; }
  }).length;

  setOldFormsTodayCount(count);
}
  async function fetchFormsToday() {
    // strictly match today's local calendar date
    const todayLocalYMD = ymdLocal(new Date());
    const url = `${API_BASE}/ghl/formSubmissions?${new URLSearchParams({ start: todayLocalYMD, end: todayLocalYMD }).toString()}`;
    const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!r.ok) throw new Error(`GHL(today) error ${r.status}`);
    const j = await r.json();
    const list = Array.isArray(j.submissions) ? j.submissions : [];

    const count = list.filter(s => {
      try { return ymdLocal(s.createdAt) === todayLocalYMD; }
      catch { return false; }
    }).length;

    setFormsTodayCount(count);
  }

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError('');
await Promise.all([
  fetchBitlyAndGHLWindow(),
  fetchFormsToday(),
  fetchOldFormsToday(),
  fetchClicksToday().then(setBitlyToday),
  fetchUserStats(), // 👈 added
]);

} catch (e) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [start, end]);

  // ---- UI -------------------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f6f8fb',
        p: { xs: 2, md: 3, },
      }}
    >

      <Box sx={{ mb: 2, mt:-10 }}>
        <Stack direction="row" spacing={1} alignItems="center">
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      bgcolor: 'green',
      animation: `${pulse} 2s infinite`
    }}
  />
  <Typography variant="caption" fontWeight={700}>LIVE</Typography>
</Stack>
  
    
      </Box>
      


{/* 🔥 Unified Sticky KPI Section */}
<Box sx={{ position: 'sticky', top: 8, zIndex: 10, mb: 2 }}>
  <Grid container spacing={2}>
    {/* Clicks Today (Bitly) */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TrendingUpIcon fontSize="small" color="primary" />
          <Typography variant="overline" color="text.secondary">Clicks Today</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {totals.clicksToday.toLocaleString()}
        </Typography>
      </Paper>
    </Grid>

    {/* Elevate Forms Submitted */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AssignmentTurnedInIcon fontSize="small" color="success" />
          <Typography variant="overline" color="text.secondary">Elevate Forms Submitted</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {formsTodayCount.toLocaleString()}
        </Typography>
      </Paper>
    </Grid>

    {/* ROAS Forms Submitted */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BarChartIcon fontSize="small" color="warning" />
          <Typography variant="overline" color="text.secondary">ROAS Forms Submitted</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {oldFormsTodayCount.toLocaleString()}
        </Typography>
      </Paper>
    </Grid>

    {/* Conversion % */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TrackChangesIcon fontSize="small" color="secondary" />
          <Typography variant="overline" color="text.secondary">Elevate Conversion %</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>{formsPerClick.pctText}</Typography>
      </Paper>
    </Grid>

    {/* James Link */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LinkIcon fontSize="small" color="success" />
          <Typography variant="overline" color="text.secondary">James Clicks Today</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {links[0] ? (clicksTodayFromDaily(links[0].daily) ?? 0).toLocaleString() : '—'}
        </Typography>
      </Paper>
    </Grid>

    {/* Anthony Link */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <LinkIcon fontSize="small" color="info" />
          <Typography variant="overline" color="text.secondary">Anthony Clicks Today</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {links[1] ? (clicksTodayFromDaily(links[1].daily) ?? 0).toLocaleString() : '—'}
        </Typography>
      </Paper>
    </Grid>

    {/* Total Users */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupIcon fontSize="small" color="primary" />
          <Typography variant="overline" color="text.secondary">Total Users</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {loadingUsers ? 0 : userTotal.toLocaleString()}
        </Typography>
      </Paper>
    </Grid>

    {/* New Users Today */}
    <Grid item xs={12} md={3}>
      <Paper sx={cardSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonAddAlt1Icon fontSize="small" color="secondary" />
          <Typography variant="overline" color="text.secondary">New Users Today</Typography>
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {loadingUsers || usersTodayCount === null ? 0 : usersTodayCount.toLocaleString()}
        </Typography>
      </Paper>
    </Grid>
  </Grid>
</Box>




      {/* Controls */}
      {/* <Paper sx={{ ...cardSx, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs="auto">
            <Stack direction="row" spacing={1}>
              {presets.map(p => (
                <Chip
                  key={p.key}
                  label={p.label}
                  color={preset === p.key ? 'default' : 'default'}
                  variant={preset === p.key ? 'filled' : 'outlined'}
                  onClick={() => setPreset(p.key)}
                />
              ))}
            </Stack>
          </Grid>

          {preset === 'custom' && (
            <Grid item xs={12} md="auto">
              <Stack direction={{ xs:'column', md:'row' }} spacing={2}>
                <TextField type="date" label="Start" value={customStart} onChange={(e)=>setCustomStart(e.target.value)} InputLabelProps={{ shrink:true }} />
                <TextField type="date" label="End" value={customEnd} onChange={(e)=>setCustomEnd(e.target.value)} InputLabelProps={{ shrink:true }} />
              </Stack>
            </Grid>
          )}

          <Grid item xs />
          <Grid item>
            <Button
              onClick={fetchAll}
              startIcon={<RefreshIcon />}
              disabled={loading}
              variant="contained"
              sx={{
                textTransform: 'none',
                borderRadius: 3,
                boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
              }}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Grid>
        </Grid>
      </Paper> */}

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        

        <Grid item xs={12} md={6}>
          <Paper sx={cardSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Elevate GHL Forms</Typography>
              <Tooltip title="Forms per local day with cumulative growth">
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={
                  (() => {
                    const map = new Map();
                    formsDaily.forEach(d => map.set(d.date, { date: d.date, count: d.count }));
                    formsCumulative.forEach(d => {
                      map.set(d.date, { ...(map.get(d.date) || { date: d.date, count: 0 }), total: d.total });
                    });
                    const arr = [...map.values()];
                    arr.sort((a, b) => a.date.localeCompare(b.date));
                    return arr;
                  })()
                }>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RTooltip />
                  <Legend />
                  <Bar dataKey="count" name="Forms / day" fill="#82ca9d" />
                  <Line type="monotone" dataKey="total" name="Cumulative" stroke="#8884d8" dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        {/* <Grid item xs={12} >
          <Paper sx={cardSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Daily Clicks (Bitly)</Typography>
              <Tooltip title="Sum of all vanity link clicks per UTC day">
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={clicksDaily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#8884d8" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid> */}
        <Grid item xs={12} md={6}>
  <Paper sx={cardSx}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        ROAS GHL Forms
      </Typography>
      <Tooltip title="Forms per local day with cumulative growth (old endpoint)">
        <InfoOutlinedIcon fontSize="small" />
      </Tooltip>
    </Stack>
    <Divider sx={{ my: 1.5 }} />
    <Box sx={{ height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={(() => {
            const map = new Map();
            oldFormsDaily.forEach(d => map.set(d.date, { date: d.date, count: d.count }));
            oldFormsCumulative.forEach(d => {
              map.set(d.date, { ...(map.get(d.date) || { date: d.date, count: 0 }), total: d.total });
            });
            const arr = [...map.values()];
            arr.sort((a, b) => a.date.localeCompare(b.date));
            return arr;
          })()}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis />
          <RTooltip />
          <Legend />
          <Bar dataKey="count" name="Forms / day" fill="#f59e0b" /> {/* orange for distinction */}
          <Line type="monotone" dataKey="total" name="Cumulative" stroke="#2563eb" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  </Paper>

</Grid>
      </Grid>
        <Grid item xs={12} sx={{ mb: 3 }}>
          <Paper sx={cardSx}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Daily Clicks (Bitly)</Typography>
              <Tooltip title="Sum of all vanity link clicks per UTC day">
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={clicksDaily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RTooltip />
                  <Legend />
                  <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#8884d8" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

      {/* Tables */}
<Grid container spacing={2}>
  {/* GHL Submissions (Window) */}
  <Grid item xs={12} md={6}>
    <Paper sx={cardSx}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Elevate GHL Submissions</Typography>
        <Tooltip title="Name, ID, phone, and created time for submissions in the selected window.">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Stack>
      <Divider sx={{ my:1.5 }} />
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(submissions || []).slice(0, 25).map(s => {
              const name = s.name || `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || '—';
              const phone = s.phone ?? s.phone_number ?? s.phoneNumber ?? s.contact_phone;
              return (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                    {s.email && (
                      <Typography variant="caption" color="text.secondary" display="block">{s.email}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtPhone(phone)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.id}</TableCell>
                  <TableCell>{fmtDate(s.createdAt)}</TableCell>
                </TableRow>
              );
            })}
            {(!submissions || submissions.length === 0) && !loading && (
              <TableRow><TableCell colSpan={4} align="center">No submissions in this window.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>

    {/* 🔥 Bitly Vanity Links under Elevate GHL */}
    <Paper sx={{ ...cardSx, mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Bitly Vanity Links</Typography>
        <Tooltip title="Only the named (vanity) links we care about">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Stack>
      <Divider sx={{ my:1.5 }} />
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Link</TableCell>
              <TableCell align="right">Clicks today</TableCell>
              <TableCell align="right">Clicks total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map(row => {
              const display = row.display || row.vanity || row.bitlink;
              return (
                <TableRow key={row.vanity || row.bitlink} hover>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <a
                      href={`https://${display}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {display}
                      </Typography>
                    </a>
                    {row.bitlink && row.vanity && row.vanity !== row.bitlink ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Canonical: {row.bitlink}
                      </Typography>
                    ) : null}
                    {row.createdAt ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created: {fmtDate(row.createdAt)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  {/* ✅ Accurate UTC-based today clicks */}
                  <TableCell align="right">{clicksTodayFromDaily(row.daily) ?? 0}</TableCell>
                  <TableCell align="right">{row.clicksTotal ?? 0}</TableCell>
                </TableRow>
              );
            })}
            {links.length === 0 && !loading && (
              <TableRow><TableCell colSpan={3} align="center">No vanity links found.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Grid>

  {/* GHL Submissions (Old Endpoint) */}
  <Grid item xs={12} md={6}>
    <Paper sx={cardSx}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>ROAS GHL Submissions</Typography>
        <Tooltip title="Name, ID, phone, and created time from the OLD GHL endpoint.">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      </Stack>
      <Divider sx={{ my:1.5 }} />
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(oldSubmissions || []).slice(0, 25).map(s => {
              const name = s.name || `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || '—';
              const phone = s.phone ?? s.phone_number ?? s.phoneNumber ?? s.contact_phone;
              return (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{name}</Typography>
                    {s.email && (
                      <Typography variant="caption" color="text.secondary" display="block">{s.email}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtPhone(phone)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{s.id}</TableCell>
                  <TableCell>{fmtDate(s.createdAt)}</TableCell>
                </TableRow>
              );
            })}
            {(!oldSubmissions || oldSubmissions.length === 0) && !loading && (
              <TableRow><TableCell colSpan={4} align="center">No submissions from old endpoint.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Grid>
</Grid>
    </Box>
    
  );
} 